import * as Api from '@ViewModels';
import { useCallback } from 'react';
import { useStepSave, useStepSaveWithOnSave, type BaseStepOverrides } from './useStepSaveFactory';
import { useAutoSave } from './useAutoSave';

interface UseTextingStepSaveOptions {
	textingStep: Api.ITextingAutomationStep | null;
	stepId: string | undefined;
	templateId: string | undefined;
	skipStepContactFilter: Api.IContactsFilterRequest | null | undefined;
	autoSave: ReturnType<typeof useAutoSave>;
}

interface UseTextingStepSaveOverrides extends BaseStepOverrides {
	newContactFilter?: Api.IContactsFilterRequest | null;
	newSendFrom?: Api.ISendFromOptions | null;
	newSendAttachmentsAsLink?: boolean;
}

interface UseTextingStepSaveReturn {
	save: (overrides?: UseTextingStepSaveOverrides) => Promise<void>;
	onSaveStep: (
		stepToSave?: Partial<Api.ITextingAutomationStep>,
		newSchedule?: Api.IAutomationStepSchedule
	) => Promise<void>;
	saveSenderOptions: (option: Api.ISendFromOptions, sendToSender: boolean) => void;
}

/**
 * Custom hook for managing texting step save operations.
 * Handles save logic for step content, contact filters, and sender options.
 */
export function useTextingStepSave({
	textingStep,
	stepId,
	templateId,
	skipStepContactFilter,
	autoSave,
}: UseTextingStepSaveOptions): UseTextingStepSaveReturn {
	const buildUpdatedStep = useCallback(
		(
			step: Api.ITextingAutomationStep | null,
			overrides: UseTextingStepSaveOverrides
		): Partial<Api.ITextingAutomationStep> => {
			const effectiveContactFilter =
				overrides.newContactFilter !== undefined ? overrides.newContactFilter : skipStepContactFilter;

			const updatedStep: Partial<Api.ITextingAutomationStep> = {
				...step,
				contactFilter: effectiveContactFilter,
			};

			if (overrides.newSendFrom !== undefined) {
				updatedStep.sendFromOptions = overrides.newSendFrom;
			}

			if (overrides.newSendAttachmentsAsLink !== undefined) {
				updatedStep.sendAttachmentsAsLink = overrides.newSendAttachmentsAsLink || undefined;
			}

			return updatedStep;
		},
		[skipStepContactFilter]
	);

	const { save } = useStepSave<Api.ITextingAutomationStep, UseTextingStepSaveOverrides>({
		step: textingStep,
		stepId,
		stepType: Api.AutomationStepType.Texting,
		templateId,
		autoSave,
		buildUpdatedStep,
	});

	const { onSaveStep } = useStepSaveWithOnSave<Api.ITextingAutomationStep>({
		step: textingStep,
		stepId,
		stepType: Api.AutomationStepType.Texting,
		templateId,
		autoSave,
	});

	const saveSenderOptions = useCallback(
		(option: Api.ISendFromOptions, _sendToSender: boolean) => {
			save({ newSendFrom: option });
		},
		[save]
	);

	return {
		save,
		onSaveStep,
		saveSenderOptions,
	};
}
